/**
 * AbnRegistryAdminPage
 * ────────────────────
 * Admin panel for the central ABN registry (hc_entities.abn_registry).
 *
 * Tabs:
 *   • Registry table — paginated + searchable list of every ABN entity
 *   • Audit log     — recent writes across the whole registry
 *   • Backfill stats — directory backfill telemetry
 *
 * Row actions:
 *   • Refresh — force a live ABR fetch (POST /api/abn/{abn}/refresh)
 *   • Audit  — open audit drawer for that ABN (GET /api/abn/{abn}/audit)
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Search,
  RefreshCw,
  ArrowLeft,
  Database,
  FileText,
  BarChart3,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const PAGE_SIZE = 25;

const MODULE_KEYS = ["directory", "plugin", "plate", "plaza", "property", "deals", "chat", "music"];

export default function AbnRegistryAdminPage() {
  const [tab, setTab] = useState("registry");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [backfill, setBackfill] = useState(null);
  const [refreshingAbn, setRefreshingAbn] = useState(null);
  const [drawerAbn, setDrawerAbn] = useState(null);
  const [drawerEvents, setDrawerEvents] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const loadRegistry = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (q.trim()) params.set("q", q.trim());
      if (moduleFilter) params.set("module", moduleFilter);
      const r = await fetch(`${API}/api/abn/registry?${params}`, { credentials: "include" });
      if (r.status === 401 || r.status === 403) {
        setLoading(false);
        setRows([]);
        setTotal(0);
        return;
      }
      const d = await r.json();
      setRows(d.rows || []);
      setTotal(d.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, q, moduleFilter]);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/abn/audit?limit=100`, { credentials: "include" });
      if (r.status === 401 || r.status === 403) {
        setLoading(false);
        setAuditEvents([]);
        return;
      }
      const d = await r.json();
      setAuditEvents(d.events || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBackfill = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/abn/backfill/status`);
      setBackfill(await r.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const tasks = { registry: loadRegistry, audit: loadAudit, stats: loadBackfill };
    const fn = tasks[tab];
    if (fn) fn();
  }, [tab, loadRegistry, loadAudit, loadBackfill]);

  const refreshAbn = async (abn) => {
    setRefreshingAbn(abn);
    try {
      await fetch(`${API}/api/abn/${abn}/refresh`, { method: "POST" });
      await loadRegistry();
    } finally {
      setRefreshingAbn(null);
    }
  };

  const openAuditDrawer = async (abn) => {
    setDrawerAbn(abn);
    setDrawerLoading(true);
    setDrawerEvents([]);
    try {
      const r = await fetch(`${API}/api/abn/${abn}/audit?limit=50`);
      const d = await r.json();
      setDrawerEvents(d.events || []);
    } finally {
      setDrawerLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showPagination = tab === "registry" && totalPages > 1;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="abn-registry-admin">
      <header className="border-b border-stone-200 bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/plaza-settings"
              className="inline-flex items-center gap-1 text-[12px] font-bold text-stone-600 hover:text-stone-900"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Admin
            </Link>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700">
                HustlePlaza · admin
              </p>
              <h1 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                <Database className="w-5 h-5 text-stone-600" />
                ABN Registry
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
            <TabButton id="registry" active={tab} onClick={setTab} icon={<Database className="w-3.5 h-3.5" />}>
              Registry
            </TabButton>
            <TabButton id="audit" active={tab} onClick={setTab} icon={<FileText className="w-3.5 h-3.5" />}>
              Audit log
            </TabButton>
            <TabButton id="stats" active={tab} onClick={setTab} icon={<BarChart3 className="w-3.5 h-3.5" />}>
              Stats
            </TabButton>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {tab === "registry" && (
          <>
            <div className="bg-white border border-stone-200 rounded-xl p-4 mb-5 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  data-testid="abn-search-input"
                  type="text"
                  value={q}
                  onChange={(e) => { setPage(0); setQ(e.target.value); }}
                  placeholder="Search entity name, trading name, ABN, ACN…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/30"
                />
              </div>
              <select
                data-testid="abn-module-filter"
                value={moduleFilter}
                onChange={(e) => { setPage(0); setModuleFilter(e.target.value); }}
                className="px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm"
              >
                <option value="">All modules</option>
                {MODULE_KEYS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <button
                data-testid="abn-refresh-list"
                onClick={loadRegistry}
                disabled={loading}
                className="px-3 py-2 rounded-lg bg-stone-900 text-white text-[13px] font-bold hover:bg-stone-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Refresh
              </button>
              <span className="text-[12px] text-stone-500 ml-auto">
                {total.toLocaleString()} entities · page {page + 1} / {totalPages}
              </span>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr className="text-left text-[10px] uppercase tracking-wider font-bold text-stone-600">
                    <th className="px-3 py-2.5">ABN</th>
                    <th className="px-3 py-2.5">Entity</th>
                    <th className="px-3 py-2.5">Trading names</th>
                    <th className="px-3 py-2.5">Modules</th>
                    <th className="px-3 py-2.5">Refreshed</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && !loading && (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-stone-500 text-[13px]">No entities match.</td></tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.abn} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`abn-row-${row.abn}`}>
                      <td className="px-3 py-2.5 font-mono text-[12px] tabular-nums">{row.abn}</td>
                      <td className="px-3 py-2.5 font-bold text-stone-900">{row.entity_name || "—"}</td>
                      <td className="px-3 py-2.5 text-stone-600">
                        {(row.trading_names || []).slice(0, 2).join(", ")}
                        {(row.trading_names || []).length > 2 && <span className="text-stone-400"> +{row.trading_names.length - 2}</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(row.modules || {})
                            .filter(([, arr]) => Array.isArray(arr) && arr.length > 0)
                            .map(([k, arr]) => (
                              <span key={k} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300">
                                {k}{arr.length > 1 ? ` ×${arr.length}` : ""}
                              </span>
                            ))}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-[11px] text-stone-500 whitespace-nowrap">
                        {row.last_abr_refresh_at ? new Date(row.last_abr_refresh_at).toLocaleDateString("en-AU") : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <button
                          data-testid={`abn-refresh-${row.abn}`}
                          onClick={() => refreshAbn(row.abn)}
                          disabled={refreshingAbn === row.abn}
                          className="px-2 py-1 rounded text-[11px] font-bold text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1"
                          title="Force fresh ABR fetch"
                        >
                          {refreshingAbn === row.abn ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                          Refresh
                        </button>
                        <button
                          data-testid={`abn-audit-${row.abn}`}
                          onClick={() => openAuditDrawer(row.abn)}
                          className="px-2 py-1 rounded text-[11px] font-bold text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" /> Audit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showPagination && (
              <div className="mt-4 flex items-center justify-between text-[12px] text-stone-600">
                <span>Showing {page * PAGE_SIZE + 1} – {Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}</span>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="abn-page-prev"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-2 py-1 rounded border border-stone-300 disabled:opacity-40 inline-flex items-center gap-1"
                  >
                    <ChevronLeft className="w-3 h-3" /> Prev
                  </button>
                  <button
                    data-testid="abn-page-next"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-2 py-1 rounded border border-stone-300 disabled:opacity-40 inline-flex items-center gap-1"
                  >
                    Next <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {tab === "audit" && (
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
              <h2 className="text-[14px] font-bold text-stone-900">Last 100 events</h2>
              <button
                data-testid="audit-refresh"
                onClick={loadAudit}
                className="text-[12px] font-bold text-stone-700 hover:text-stone-900 inline-flex items-center gap-1"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Refresh
              </button>
            </div>
            <table className="w-full text-[12px]">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-left text-[10px] uppercase tracking-wider font-bold text-stone-600">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">ABN</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {auditEvents.length === 0 && !loading && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-stone-500">No events.</td></tr>
                )}
                {auditEvents.map((e, i) => (
                  <tr key={i} className="border-b border-stone-100" data-testid={`audit-row-${i}`}>
                    <td className="px-3 py-2 text-stone-500 whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString("en-AU") : "—"}</td>
                    <td className="px-3 py-2"><span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-stone-100">{e.event}</span></td>
                    <td className="px-3 py-2 font-mono">{e.abn || "—"}</td>
                    <td className="px-3 py-2 text-stone-600">{e.actor || e.linked_by || "system"}</td>
                    <td className="px-3 py-2 text-stone-600 truncate max-w-[280px]">
                      {e.module ? `${e.module} → ${e.ref || "?"}` : (e.note || "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "stats" && backfill && (
          <div className="grid sm:grid-cols-2 gap-4">
            <StatCard label="Total directory rows" value={backfill.total} />
            <StatCard label="Searched" value={`${backfill.searched.toLocaleString()} (${backfill.pct_searched}%)`} />
            <StatCard label="Matched" value={backfill.matched} accent="emerald" />
            <StatCard label="Ambiguous" value={backfill.ambiguous} accent="amber" />
            <StatCard label="No match" value={backfill.no_match} accent="stone" />
            <StatCard label="Errors" value={backfill.error} accent="red" />
            <StatCard label="Verified ABNs" value={backfill.abn_status?.verified} accent="emerald" />
            <StatCard label="Not required" value={backfill.abn_status?.not_required} accent="stone" />
            <div className="sm:col-span-2 bg-white border border-stone-200 rounded-xl p-4">
              <h3 className="text-[11px] uppercase tracking-wider font-bold text-stone-600 mb-3">Listing type mix</h3>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(backfill.listing_types || {}).map(([k, v]) => (
                  <span key={k} className="px-2 py-1 rounded-full bg-stone-100 text-[11px] font-bold text-stone-700">
                    {k} · {v.toLocaleString()}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        {tab === "stats" && !backfill && (
          <div className="text-center text-stone-500 py-12">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Failed to load stats."}
          </div>
        )}
      </main>

      {drawerAbn && (
        <div className="fixed inset-0 z-30 flex justify-end" data-testid="audit-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerAbn(null)} />
          <div className="relative bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500">ABN Audit log</p>
                <h2 className="text-[14px] font-bold text-stone-900 font-mono">{drawerAbn}</h2>
              </div>
              <button onClick={() => setDrawerAbn(null)} className="text-stone-400 hover:text-stone-900" data-testid="audit-drawer-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {drawerLoading && <Loader2 className="w-4 h-4 animate-spin text-stone-400" />}
              {!drawerLoading && drawerEvents.length === 0 && (
                <p className="text-[12px] text-stone-500 text-center py-8">No events for this ABN.</p>
              )}
              {drawerEvents.map((e, i) => (
                <div key={i} className="border border-stone-200 rounded-lg p-3 text-[12px]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-stone-100">{e.event}</span>
                    <span className="text-[11px] text-stone-500">{e.at ? new Date(e.at).toLocaleString("en-AU") : ""}</span>
                  </div>
                  <p className="text-stone-700">
                    {e.module ? <><strong>{e.module}</strong> → {e.ref}</> : (e.note || "—")}
                  </p>
                  <p className="text-[11px] text-stone-500 mt-1">Actor: {e.actor || e.linked_by || "system"}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ id, active, onClick, icon, children }) {
  const isActive = active === id;
  return (
    <button
      data-testid={`abn-tab-${id}`}
      onClick={() => onClick(id)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold transition-all ${
        isActive ? "bg-white text-stone-900 shadow-sm" : "text-stone-600 hover:text-stone-900"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function StatCard({ label, value, accent = "stone" }) {
  const palette = {
    stone:   "border-stone-200 text-stone-900",
    emerald: "border-emerald-200 text-emerald-900 bg-emerald-50",
    amber:   "border-amber-200 text-amber-900 bg-amber-50",
    red:     "border-red-200 text-red-900 bg-red-50",
  };
  return (
    <div className={`bg-white border rounded-xl p-4 ${palette[accent] || palette.stone}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1 tabular-nums">{value ?? "—"}</p>
    </div>
  );
}
